import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import { CountrySetting, PartnerCountry } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import AddCountryFormView from './add-country-form-view';
import { ContainerProps, Option } from './types';

const getMinBudget = (val: Option | undefined, map: { [key: string]: number; }) => {
  if (!val) return map.default;
  if (!map[val.label]) return map.default;
  return map[val.label];
};

const AddCountryFormContainer = ({
  onAddCountry,
  onClose,
  open,
  partnerCountry,
  excludeCodes,
}: ContainerProps) => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: countrySettings, error } = useFetchImmutable<CountrySetting[]>(apiClient.countrySettingsClient.getCountrySettings, [], true);
  if (error?.code === 401) navigate('/logout');

  const {
    register,
    clearErrors,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitSuccessful },
    reset,
  } = useForm();

  const countrySettingsMap = useMemo(() => {
    const map: { [key: CountrySetting['country']]: CountrySetting['minBudgetPPPD'] } = {};
    if (countrySettings && countryCodes) {
      countrySettings.forEach((cs: CountrySetting) => {
        if (cs.isoCode2 === 'Default') map.default = cs.minBudgetPPPD;
        else map[countryCodes[cs.isoCode2]] = cs.minBudgetPPPD;
      });
    }
    return map;
  }, [countrySettings, countryCodes]);

  const countryOptions = useMemo(() => {
    const test = excludeCodes || {};
    const rawList = Object.entries(countryCodes || {}).map(([code, label]) => (
      {
        label,
        value: code,
      }
    ));
    const filterList = rawList.filter((ele) => !test[ele.value]);
    if (partnerCountry) {
      filterList.unshift({
        label: countryCodes![partnerCountry.country],
        value: partnerCountry.country,
      });
    }
    return filterList.sort((a, b) => a.label.localeCompare(b.label));
  }, [countryCodes, excludeCodes, partnerCountry]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ country: '', primary: '', minBudgetPPPD: countrySettingsMap.default });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    // Set input values to country being edited
    if (partnerCountry) {
      setValue('country', {
        label: countryCodes![partnerCountry.country],
        value: partnerCountry.country,
      });
      setValue('primary', partnerCountry.primary);
      setValue('minBudgetPPPD', partnerCountry.minBudgetPPPD);
    }
  }, [partnerCountry]);

  const setMinBudgetPPPD = (data:Option) => {
    const min = getMinBudget(data, countrySettingsMap);
    setValue('minBudgetPPPD', min);
    clearErrors('minBudgetPPPD');
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const {
      minBudgetPPPD,
      primary,
      country: {
        value,
      },
    } = data;
    const ret = {
      minBudgetPPPD,
      primary,
      country: value,
    };
    onAddCountry(ret as PartnerCountry);
    reset({ country: '', primary: '', minBudgetPPPD: countrySettingsMap.default });
    onClose();
  };

  const handleCancel = () => {
    reset({ country: '', primary: '', minBudgetPPPD: countrySettingsMap.default });
    onClose();
  };

  return (
    <AddCountryFormView
      control={control}
      countryOptions={countryOptions}
      errors={errors}
      handleSubmit={handleSubmit}
      open={open}
      onCancel={handleCancel}
      onSubmit={onSubmit}
      register={register}
      setMinBudgetPPPD={setMinBudgetPPPD}
      partnerCountry={partnerCountry}
      defaultBudget={countrySettingsMap.default}
    />
  );
};

export default AddCountryFormContainer;
